import React, {MouseEvent, useContext, useRef} from 'react'

import Popover, {PopoverRef} from '../overlays/Popover.js'
import ListItem from '../surfaces/ListItem.js'
import FormRowContext, {FieldRowReset, getFieldRowOffset, markFieldRowControl} from '../forms/FormRowContext.js'
import Button, {ButtonProps} from './Button.js'
import {buttonSizes} from './styles.js'

export interface MenuButtonOption {
    label?: string
    value?: string
    disabled?: boolean
    /** Gets the MenuButton's index as second argument */
    onClick?: (event: MouseEvent, index?: number) => void
}

export interface MenuButtonProps extends Omit<ButtonProps, 'onClick' | 'afterIcon'> {
    /** Falsy entries are skipped, so options can be added conditionally */
    options?: (MenuButtonOption | false | null | undefined)[]
    /** Passed to each option's onClick, for options shared by several MenuButtons (one per address) */
    index?: number
}

/**
 * A Button with an arrow that opens a menu of actions, each calls its own onClick. The style prop places the button,
 * the menu opens below it.
 */
function MenuButton({options, index, style = {}, size = 'md', disabled, ...rest}: MenuButtonProps) {
    const popoverRef = useRef<PopoverRef>(null)
    const inTopAlignedRow = useContext(FormRowContext)

    const height = typeof style.height === 'number' ? style.height : buttonSizes.height[size]

    // The offset goes on the outer element, unless the caller placed the button itself
    const rowOffset = style.marginTop === undefined && style.margin === undefined ? getFieldRowOffset(inTopAlignedRow, height) : {}

    // The button fills the width the caller gives the whole
    const fills = style.width !== undefined || style.flex !== undefined

    const onClickOption = (option: MenuButtonOption, event: MouseEvent) => {
        event.stopPropagation()
        option.onClick?.(event, index)
        popoverRef.current?.close()
    }

    return (
        <Popover
            ref={popoverRef}
            // The Popover's wrapper is the element in the caller's layout
            style={{...rowOffset, ...style}}
            disabled={disabled}
            content={
                <>
                    {options?.map((option, optionIndex) => option && (
                        <ListItem
                            key={`${optionIndex}button`}
                            size='sm'
                            disabled={option.disabled}
                            onClick={(event: MouseEvent) => onClickOption(option, event)}
                        >
                            {option.label || option.value}
                        </ListItem>
                    ))}
                </>
            }
        >
            {/* The button is placed by the Popover's wrapper, so it doesn't get an offset of its own */}
            <FieldRowReset>
                <Button
                    aria-haspopup='menu'
                    {...rest}
                    size={size}
                    disabled={disabled}
                    style={{...(fills && {width: '100%'}), ...(style.height !== undefined && {height: style.height})}}
                    onClick={(event) => {
                        event.stopPropagation()
                        popoverRef.current?.show()
                    }}
                    afterIcon='mdi mdi-menu-down'
                />
            </FieldRowReset>
        </Popover>
    )
}

export default markFieldRowControl(MenuButton)
